// client/hooks/use-dexscreener.ts

import { useState, useEffect } from 'react';

interface TokenFullData {
  priceUsd: number;
  priceChange24h?: number;
  symbol: string;
  address: string;
  source: 'dexscreener' | 'coingecko';
  timestamp: number;
  high24h?: number;
  low24h?: number;
  volumeToken24h?: number;
  volumeQuote24h?: number;
  chain?: string;
}

interface UseTokenPriceOptions {
  refetchInterval?: number;
}

const SOLANA_TOKEN_ADDRESSES = {
  SOL: "So11111111111111111111111111111111111111112",
  USDT: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB",
  USDC: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
  FIXERCOIN: "H4qKn8FMFha8jJuj8xMryMqRhH3h7GjLuxw7TVixpump",
  PINGX: "7KS4DgKHmgSWYC4uGnSozLUon2bDEj6WKhRNSosmpump",
  LOCKER: "EN1nYrW6375zMPUkpkGyGSEXW8WmAqYu4yhf6xnGpump",
  FXM: "7Fnx57ztmhdpL1uAGmUY1ziwPG2UDKmG6poB4ibjpump",
  BONK: "DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263",
};

const MULTI_CHAIN_TOKEN_ADDRESSES = {
  BTC: { chain: "ethereum", address: "0x2260fac5e5542a773aa44fbcfedf7c193bc2c599" },
  ETH: { chain: "ethereum", address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2" },
  DAI: { chain: "ethereum", address: "0x6b175474e89094c44da98b954eedeac495271d0f" },
  UNI: { chain: "ethereum", address: "0x1f9840a85d5af5bf1d1762f925bdaddc4201f984" },
  LINK: { chain: "ethereum", address: "0x514910771af9ca656af840dff83e8264ecf986ca" },
  BNB: { chain: "bsc", address: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c" },
  CAKE: { chain: "bsc", address: "0x0e09fabb73bd3ade0a17ecc321fd13a19e81ce82" },
  MATIC: { chain: "polygon", address: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270" },
  SHIB: { chain: "ethereum", address: "0x95aD61b0a150d79219dCF64E1E6Cc01f0B64C4cE" },
};

const TOKEN_ADDRESSES = {
  ...SOLANA_TOKEN_ADDRESSES,
  BTC: MULTI_CHAIN_TOKEN_ADDRESSES.BTC.address,
  ETH: MULTI_CHAIN_TOKEN_ADDRESSES.ETH.address,
  BNB: MULTI_CHAIN_TOKEN_ADDRESSES.BNB.address,
  MATIC: MULTI_CHAIN_TOKEN_ADDRESSES.MATIC.address,
  DAI: MULTI_CHAIN_TOKEN_ADDRESSES.DAI.address,
  UNI: MULTI_CHAIN_TOKEN_ADDRESSES.UNI.address,
  LINK: MULTI_CHAIN_TOKEN_ADDRESSES.LINK.address,
  CAKE: MULTI_CHAIN_TOKEN_ADDRESSES.CAKE.address,
  SHIB: MULTI_CHAIN_TOKEN_ADDRESSES.SHIB.address,
};

const ADDRESS_CHAIN_MAP: Record<string, string> = {
  [TOKEN_ADDRESSES.SOL]: "solana",
  [TOKEN_ADDRESSES.USDT]: "solana",
  [TOKEN_ADDRESSES.USDC]: "solana",
  [TOKEN_ADDRESSES.FIXERCOIN]: "solana",
  [TOKEN_ADDRESSES.PINGX]: "solana",
  [TOKEN_ADDRESSES.LOCKER]: "solana",
  [TOKEN_ADDRESSES.FXM]: "solana",
  [TOKEN_ADDRESSES.BONK]: "solana",
  [TOKEN_ADDRESSES.BTC]: "ethereum",
  [TOKEN_ADDRESSES.ETH]: "ethereum",
  [TOKEN_ADDRESSES.DAI]: "ethereum",
  [TOKEN_ADDRESSES.UNI]: "ethereum",
  [TOKEN_ADDRESSES.LINK]: "ethereum",
  [TOKEN_ADDRESSES.BNB]: "bsc",
  [TOKEN_ADDRESSES.CAKE]: "bsc",
  [TOKEN_ADDRESSES.MATIC]: "polygon",
  [TOKEN_ADDRESSES.SHIB]: "ethereum",
};

// Major tokens that can fallback to CoinGecko
const MAJOR_TOKENS = new Set(['BTC', 'ETH', 'BNB', 'SOL', 'MATIC', 'UNI', 'LINK', 'CAKE', 'SHIB']);

const priceCache: Record<string, { data: TokenFullData; timestamp: number }> = {};

async function fetchFromDexScreener(tokenAddress: string, attempt: number = 1): Promise<any> {
  const url = `https://api.dexscreener.com/latest/dex/tokens/${tokenAddress}`;
  const maxAttempts = 3;
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    const response = await fetch(url, { signal: controller.signal, headers: { 'Accept': 'application/json' } });
    clearTimeout(timeoutId);
    if (!response.ok) {
      if (response.status === 429 && attempt < maxAttempts) {
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchFromDexScreener(tokenAddress, attempt + 1);
      }
      throw new Error(`HTTP ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error: any) {
    if (attempt < maxAttempts && (error.name === 'AbortError' || error.message.includes('ERR_NAME_NOT_RESOLVED') || error.message.includes('Failed to fetch'))) {
      const delay = Math.pow(2, attempt) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchFromDexScreener(tokenAddress, attempt + 1);
    }
    console.error(`[DexScreener] Fetch error for ${tokenAddress}:`, error.message);
    return null;
  }
}

function extractFullData(pair: any, tokenAddress: string): TokenFullData | null {
  if (!pair) return null;
  const price = parseFloat(pair.priceUsd);
  if (isNaN(price) || price <= 0) return null;
  let change24h: number | undefined = undefined;
  if (pair.priceChange?.h24 !== undefined && pair.priceChange?.h24 !== null) {
    const raw = parseFloat(pair.priceChange.h24);
    if (!isNaN(raw)) change24h = raw;
  }
  if (change24h === undefined && pair.priceChange24h !== undefined) {
    const raw = parseFloat(pair.priceChange24h);
    if (!isNaN(raw)) change24h = raw;
  }
  if (change24h === undefined && pair.change24h !== undefined) {
    const raw = parseFloat(pair.change24h);
    if (!isNaN(raw)) change24h = raw;
  }
  let high24h: number | undefined = undefined;
  let low24h: number | undefined = undefined;
  if (pair.priceHigh24h !== undefined && pair.priceHigh24h !== null) {
    const h = parseFloat(pair.priceHigh24h);
    if (!isNaN(h)) high24h = h;
  }
  if (pair.priceLow24h !== undefined && pair.priceLow24h !== null) {
    const l = parseFloat(pair.priceLow24h);
    if (!isNaN(l)) low24h = l;
  }
  let volumeToken24h: number | undefined = undefined;
  let volumeQuote24h: number | undefined = undefined;
  if (pair.volume?.h24 !== undefined && pair.volume?.h24 !== null) {
    const vol = parseFloat(pair.volume.h24);
    if (!isNaN(vol)) volumeToken24h = vol;
  }
  if (pair.volumeQuote?.h24 !== undefined && pair.volumeQuote?.h24 !== null) {
    const volQ = parseFloat(pair.volumeQuote.h24);
    if (!isNaN(volQ)) volumeQuote24h = volQ;
  }
  let chain = pair.chainId || pair.network;
  if (!chain) chain = ADDRESS_CHAIN_MAP[tokenAddress] || "unknown";
  const symbol = pair.baseToken?.symbol?.toUpperCase() || getTokenSymbol(tokenAddress);
  return {
    priceUsd: price,
    priceChange24h: change24h,
    symbol,
    address: tokenAddress,
    source: 'dexscreener',
    timestamp: Date.now(),
    high24h,
    low24h,
    volumeToken24h,
    volumeQuote24h,
    chain,
  };
}

function getTokenSymbol(address: string): string {
  const symbolMap: Record<string, string> = {
    [TOKEN_ADDRESSES.BTC]: 'BTC',
    [TOKEN_ADDRESSES.ETH]: 'ETH',
    [TOKEN_ADDRESSES.BNB]: 'BNB',
    [TOKEN_ADDRESSES.MATIC]: 'MATIC',
    [TOKEN_ADDRESSES.DAI]: 'DAI',
    [TOKEN_ADDRESSES.UNI]: 'UNI',
    [TOKEN_ADDRESSES.LINK]: 'LINK',
    [TOKEN_ADDRESSES.CAKE]: 'CAKE',
    [TOKEN_ADDRESSES.SOL]: 'SOL',
    [TOKEN_ADDRESSES.USDT]: 'USDT',
    [TOKEN_ADDRESSES.USDC]: 'USDC',
    [TOKEN_ADDRESSES.FIXERCOIN]: 'FIXERCOIN',
    [TOKEN_ADDRESSES.PINGX]: 'PINGX',
    [TOKEN_ADDRESSES.LOCKER]: 'LOCKER',
    [TOKEN_ADDRESSES.FXM]: 'FXM',
    [TOKEN_ADDRESSES.BONK]: 'BONK',
    [TOKEN_ADDRESSES.SHIB]: 'SHIB',
  };
  return symbolMap[address] || address.slice(0, 4);
}

// CoinGecko fallback for major tokens only
async function fetchCoinGeckoPrice(tokenSymbol: string): Promise<number | null> {
  const symbolToId: Record<string, string> = {
    'BTC': 'bitcoin',
    'ETH': 'ethereum',
    'BNB': 'binancecoin',
    'SOL': 'solana',
    'MATIC': 'matic-network',
    'UNI': 'uniswap',
    'LINK': 'chainlink',
    'CAKE': 'pancakeswap-token',
    'SHIB': 'shiba-inu',
  };
  const coinId = symbolToId[tokenSymbol];
  if (!coinId) return null;
  try {
    const response = await fetch(
      `https://api.coingecko.com/api/v3/simple/price?ids=${coinId}&vs_currencies=usd`,
      { headers: { 'accept': 'application/json' } }
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data[coinId]?.usd || null;
  } catch (err) {
    console.warn(`[CoinGecko] Error for ${tokenSymbol}:`, err);
    return null;
  }
}

// Main hook – only DexScreener + CoinGecko for major tokens
export function useMultipleTokenPrices(
  tokenAddresses: string[],
  options: UseTokenPriceOptions = {}
) {
  const [prices, setPrices] = useState<Record<string, TokenFullData>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAllPrices = async () => {
    if (!tokenAddresses.length) return;
    setLoading(true);
    setError(null);

    try {
      const results: Record<string, TokenFullData> = {};
      const currentTime = Date.now();

      await Promise.all(
        tokenAddresses.map(async (address) => {
          // Check cache (30 seconds)
          const cached = priceCache[address];
          if (cached && currentTime - cached.timestamp < 30000) {
            results[address] = cached.data;
            return;
          }

          let tokenData: TokenFullData | null = null;
          const symbol = getTokenSymbol(address);

          // 1. Try DexScreener
          try {
            const dexData = await fetchFromDexScreener(address);
            if (dexData?.pairs?.length) {
              const sortedPairs = dexData.pairs.sort((a: any, b: any) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0));
              const bestPair = sortedPairs[0];
              const extracted = extractFullData(bestPair, address);
              if (extracted && extracted.priceUsd > 0) {
                tokenData = extracted;
              }
            }
          } catch (err) {
            console.warn(`[DexScreener] Failed for ${address}:`, err);
          }

          // 2. If DexScreener failed and token is major, try CoinGecko
          if (!tokenData && MAJOR_TOKENS.has(symbol)) {
            const price = await fetchCoinGeckoPrice(symbol);
            if (price && price > 0) {
              tokenData = {
                priceUsd: price,
                symbol,
                address,
                source: 'coingecko',
                timestamp: currentTime,
                chain: ADDRESS_CHAIN_MAP[address] || 'unknown',
              };
            }
          }

          // No fallback for other tokens (including FIXERCOIN, PINGX, LOCKER, FXM)
          if (tokenData) {
            priceCache[address] = { data: tokenData, timestamp: currentTime };
            results[address] = tokenData;
          }
        })
      );

      setPrices(results);
    } catch (err: any) {
      console.error("[useMultipleTokenPrices] Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPrices();
    if (options.refetchInterval && options.refetchInterval > 0) {
      const interval = setInterval(fetchAllPrices, options.refetchInterval);
      return () => clearInterval(interval);
    }
  }, [JSON.stringify(tokenAddresses), options.refetchInterval]);

  return { prices, loading, error, refetch: fetchAllPrices };
}

export const useMultipleTokens = useMultipleTokenPrices;
export type { TokenFullData, UseTokenPriceOptions };
